import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Lock, X, Users, Sparkles } from "lucide-react";
import PremiumBenefitsModal from "./PremiumBenefitsModal";

/**
 * 정회원 이상만 회원목록을 열람할 수 있도록 안내하는 따뜻한 가드 모달.
 * Props:
 *  - open
 *  - onClose
 *  - reason: "auth" (비로그인) | "upgrade" (basic 회원 등 권한 부족)
 */
export default function MembershipGuardModal({ open, onClose, reason = "upgrade" }) {
  const navigate = useNavigate();
  const [showBenefits, setShowBenefits] = useState(false);

  // ESC로 닫기
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleLogin = () => {
    // Dispatch first, then close — avoids AnimatePresence unmount race
    window.dispatchEvent(new CustomEvent("openAuthModal", { detail: { mode: "login" } }));
    onClose?.();
  };

  const handleUpgrade = () => {
    // 정회원 혜택보기 큰 창을 띄움 (가드 모달은 닫음)
    setShowBenefits(true);
    onClose?.();
  };

  const COPY = {
    auth: {
      title: "로그인이 필요해요",
      subtitle: "쪽지·회원 정보는 로그인하신 분들께만 열려있어요. 따뜻한 인연을 시작해보시겠어요?",
    },
    upgrade: {
      title: "정회원 전용 공간이에요",
      subtitle: "정회원이 되시면 글로벌 중년 싱글 회원분들의 프로필을 자유롭게 만나볼 수 있어요.",
    },
    dm_upgrade: {
      title: "쪽지는 정회원 전용이에요",
      subtitle: "정회원으로 업그레이드하시면 서로 자유롭게 따뜻한 이야기를 나눌 수 있어요.",
    },
  };
  const { title, subtitle } = COPY[reason] || COPY.upgrade;

  return (
    <>
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
          data-testid="membership-guard-backdrop"
        >
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            data-testid="membership-guard-modal"
            role="dialog"
            aria-modal="true"
          >
            {/* Hero */}
            <div className="relative px-7 pt-8 pb-6 bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors"
                aria-label="닫기"
                data-testid="membership-guard-close"
              >
                <X className="w-4 h-4 text-stone-500" />
              </button>

              <div className="flex justify-center mb-5">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-burgundy-500 to-rose-400 text-white flex items-center justify-center shadow-md">
                    <Users className="w-7 h-7" strokeWidth={1.5} />
                  </div>
                  <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-amber-400 text-white flex items-center justify-center shadow-sm">
                    <Lock className="w-3.5 h-3.5" strokeWidth={2} />
                  </div>
                </div>
              </div>

              <h2 className="font-serif text-2xl text-stone-900 text-center tracking-tight" data-testid="membership-guard-title">
                {title}
              </h2>
              <p className="text-[15px] text-stone-600 text-center mt-3 leading-relaxed">
                {subtitle}
              </p>
            </div>

            {/* Benefits */}
            <div className="px-7 py-5 bg-white space-y-2.5 border-y border-stone-100">
              <div className="text-xs font-bold text-stone-400 tracking-[0.15em] uppercase mb-2">정회원 혜택</div>
              {[
                "회원 프로필 자유 열람",
                "주1회 글로벌 줌미팅 참여",
                "쪽지/관심표현 무제한",
                "AI 매칭 추천 우선 노출",
                "오프라인 모임 우선 신청 및 20% D/C 적용",
              ].map((b) => (
                <div key={b} className="flex items-start gap-2.5 text-[14px] text-stone-700">
                  <Sparkles className="w-4 h-4 text-burgundy-500 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{b}</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="px-7 py-5 space-y-2">
              {reason === "auth" ? (
                <button
                  onClick={handleLogin}
                  className="w-full py-3.5 rounded-2xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold text-[15px] transition-colors shadow-sm"
                  data-testid="membership-guard-login-btn"
                >
                  로그인 / 회원가입
                </button>
              ) : (
                <button
                  onClick={handleUpgrade}
                  className="w-full py-3.5 rounded-2xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold text-[15px] transition-colors shadow-sm"
                  data-testid="membership-guard-upgrade-btn"
                >
                  정회원 혜택보기 →
                </button>
              )}
              <button
                onClick={onClose}
                className="w-full py-2.5 text-sm text-stone-500 hover:text-stone-700 transition-colors"
                data-testid="membership-guard-cancel-btn"
              >
                나중에 둘러볼게요
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    <PremiumBenefitsModal open={showBenefits} onClose={() => setShowBenefits(false)} />
    </>
  );
}

/**
 * 회원목록 열람 권한 체크
 * - 정회원(regular) / 프리미엄(legacy) / VIP / admin 허용
 * - basic / null / 비로그인 거부
 */
export function canViewMembers(user) {
  if (!user) return false;
  if (user.role === "admin") return true;
  const tier = (user.membership_type || user.membershipType || "basic").toLowerCase();
  return ["regular", "premium", "vip"].includes(tier);
}

/**
 * 쪽지(DM) 발송 권한 체크
 * - 일반회원(basic)도 쪽지 발송 가능 (백엔드에서 최대 50회 제한 적용)
 * - 비로그인만 차단
 */
export function canSendDM(user) {
  return !!user;
}
